import { Injectable, signal } from '@angular/core';
import { MenuNode } from './menu-tree';

/** Where earlier versions remembered the open sections; cleared so they no longer reopen on reload. */
const LEGACY_STORAGE_KEY = 'em-fe:sidebar-menu:expanded';

/**
 * Expanded/collapsed state of the sidebar tree, keyed by node path.
 * Everything starts collapsed on each page load; choices last for the session (while navigating),
 * and the branch of the current page is opened by the menu.
 */
@Injectable({
  providedIn: 'root',
})
export class MenuService {
  private readonly expandedState = signal<Readonly<Record<string, boolean>>>({});

  /** Keys of the branches leading to the current route. */
  readonly activeTrail = signal<ReadonlySet<string>>(new Set());

  /** The visible menu (permission-filtered), shared with the header and the command palette. */
  readonly tree = signal<readonly MenuNode[]>([]);

  /** Menu path of the current page, from its section down (drives the header breadcrumb). */
  readonly activePath = signal<readonly MenuNode[]>([]);

  constructor() {
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch { /* storage may be unavailable: nothing to clean */ }
  }

  isExpanded(key: string): boolean {
    return this.expandedState()[key] ?? false;
  }

  toggle(key: string): void {
    this.update({ [key]: !this.isExpanded(key) });
  }

  setExpanded(keys: Iterable<string>, expanded: boolean): void {
    const changes: Record<string, boolean> = {};
    for (const key of keys) changes[key] = expanded;
    this.update(changes);
  }

  /** Collapses everything when anything is open, otherwise expands everything (Ctrl/⌘ I). */
  toggleAll(keys: readonly string[]): void {
    this.setExpanded(keys, !keys.some((key) => this.isExpanded(key)));
  }

  private update(changes: Record<string, boolean>): void {
    const current = this.expandedState();
    if (Object.entries(changes).every(([key, value]) => current[key] === value)) return;
    this.expandedState.set({ ...current, ...changes });
  }
}
